import React, { useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import Divider from "@mui/material/Divider";
import { EncircledPlusIcon } from "../../../../utils/svg-components";
import { Button } from "../../../../components/button/button";
import SortableAccordian from "../../../../components/accordian/SortableAccordian";
import { getDateRange } from "../milestone.utils";
import { ALERT_MESSAGES } from "../../planner.constants";
import TaskHeader from "./TaskHeader";
import TaskBody from "./TaskBody";

// This component renders the list of tasks with drag & drop support.
const MilestoneBodyList = ({
  milestone,
  resources,
  builderMilestoneTasks,
  isGeneralUser,
  milestoneIndex,
  handleTaskChange,
  handleDateRangeChange,
  onChangeResponsibility,
  handleMilestoneChange,
  handleDelete,
  handleDeliverableUpdate,
  handleStatusChange,
  project,
}) => {
  const [expanded, setExpanded] = useState([]);

  const handleAccordionChange = (panelId) => {
    setExpanded((prevExpanded) =>
      prevExpanded.includes(panelId)
        ? prevExpanded.filter((p) => p !== panelId)
        : [...prevExpanded, panelId]
    );
  };

  // Handle drag & drop reordering of tasks within a milestone.
  const handleTaskDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = milestone.tasks.findIndex((task) => task.id === active.id);
    const newIndex = milestone.tasks.findIndex((task) => task.id === over.id);
    const updatedTasks = arrayMove(milestone.tasks, oldIndex, newIndex);

    // Update the tasks order in the parent state
    handleMilestoneChange(milestoneIndex, "tasks", updatedTasks);
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleTaskDragEnd}
    >
      <SortableContext
        items={milestone.tasks.map((task) => task.id)}
        strategy={verticalListSortingStrategy}
      >
        {milestone.tasks.map((task, taskIndex) => {
          const { defaultDateRange } = getDateRange(
            task,
            builderMilestoneTasks,
            resources
          );
          const newBuilderTaskHours = task?.hours;
          const newBuilderTaskCost = task?.budget;

          return (
            <SortableAccordian
              key={task.id}
              id={task.id}
              name={task.id} // Use unique id instead of "task"
              handleChange={() => handleAccordionChange(task.id)}
              expanded={expanded.includes(task.id) ? task.id : ""}
              variant="light"
              arrowPosition="right"
              showDragHandle={!isGeneralUser}
              heading={
                <TaskHeader
                  isGeneralUser={isGeneralUser}
                  task={task}
                  milestoneIndex={milestoneIndex}
                  taskIndex={taskIndex}
                  handleDelete={handleDelete}
                  handleTaskChange={handleTaskChange}
                  handleDateRangeChange={handleDateRangeChange}
                  defaultDateRange={defaultDateRange}
                  onChangeResponsibility={onChangeResponsibility}
                  newBuilderTaskHours={newBuilderTaskHours}
                  newBuilderTaskCost={newBuilderTaskCost}
                />
              }
              body={
                <TaskBody
                  deliverableId={milestone.id}
                  task={task}
                  resources={resources}
                  isGeneralUser={isGeneralUser}
                  handleDeliverableUpdate={handleDeliverableUpdate}
                  handleStatusChange={handleStatusChange}
                  project={project}
                />
              }
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
};

// This component wires in the task-level business logic and renders the list.
const MilestoneBody = ({
  isGeneralUser,
  milestone,
  resources,
  builderMilestoneTasks,
  handleDeleteTask,
  handleTaskChange,
  milestoneIndex,
  handleDateRangeChange,
  handleAddTask,
  handleMilestoneChange,
  handleDeliverableUpdate,
  handleStatusChange,
  project,
}) => {
  // Business logic: handle changes to the task's "responsibility" field.
  const onChangeResponsibility = (
    taskIndex,
    e,
    newBuilderTaskHours,
    newBuilderTaskCost
  ) => {
    const selectedResponsibility = e.target.value;
    const isTaskAllocated = newBuilderTaskHours > 0 || newBuilderTaskCost > 0;

    if (isTaskAllocated && selectedResponsibility === "Client") {
      window.alert(ALERT_MESSAGES.ChangeResponsibility);
      return;
    }

    // Proceed with updating the task.
    handleTaskChange(
      milestoneIndex,
      taskIndex,
      "organization",
      selectedResponsibility
    );
  };

  // Business logic: handle deletion of a task.
  const handleDelete = (taskIndex) => {
    const task = milestone.tasks[taskIndex];

    const hasValueAllocated = task.resources?.some(
      (res) => res.isEnabled && Number(res.hours) > 0
    );

    const isTaskEmpty = !hasValueAllocated;

    if (!isTaskEmpty) {
      window.alert(ALERT_MESSAGES.UnableToDeleteTask);
      return;
    }

    handleDeleteTask(milestoneIndex, taskIndex);
  };

  return (
    <div className="milestone-body">
      <MilestoneBodyList
        milestone={milestone}
        resources={resources}
        builderMilestoneTasks={builderMilestoneTasks}
        isGeneralUser={isGeneralUser}
        milestoneIndex={milestoneIndex}
        handleDeleteTask={handleDeleteTask}
        handleTaskChange={handleTaskChange}
        handleDateRangeChange={handleDateRangeChange}
        onChangeResponsibility={onChangeResponsibility}
        handleDelete={handleDelete}
        handleMilestoneChange={handleMilestoneChange}
        handleDeliverableUpdate={handleDeliverableUpdate}
        handleStatusChange={handleStatusChange}
        project={project}
      />
      {!isGeneralUser && (
        <div className="add-task-btn">
          <Button
            onClick={handleAddTask}
            startIcon={<EncircledPlusIcon />}
            size="large"
            variant="filled"
            color="tertiary"
          >
            Add Task
          </Button>
        </div>
      )}
    </div>
  );
};

export default MilestoneBody;
